import type { Passbook } from '@/shared/types';
import type { CurrencyCode } from '@/shared/lib/format';

export function quantityLabel(passbook: Passbook): string {
  if (passbook.hasTransaction || passbook.hasGiftcard) return 'Saldo';
  if (passbook.hasCashback) return 'Cashback';
  if (passbook.hasPoints) return 'Pontos';
  return 'Selos';
}

/** Moeda do tenant: hasPT/ES/EN → EUR; hasIN → GBP; senão BRL (legado verify_*). */
export function passbookCurrency(passbook: Passbook): CurrencyCode {
  if (passbook.hasIN) return 'GBP';
  if (passbook.hasPT || passbook.hasES || passbook.hasEN) return 'EUR';
  return 'BRL';
}

/** Espelha legacy/dash/app.js: Operador some quando regraCartao.integrado === true (exceto exceções). */
const OPERATOR_PASSBOOK_EXCEPTIONS = new Set([
  '680a80debdb42d4dd07a8fa8',
  '65529570f71ea24fa97880c2',
]);

export function shouldShowOperator(passbook: Passbook): boolean {
  if (OPERATOR_PASSBOOK_EXCEPTIONS.has(passbook._id)) return true;
  // Produção: 2 lojas têm integrado como string 'true' (Estação Dos Grãos, Bom Beef Burgers).
  const integ = passbook.regraCartao?.integrado as unknown;
  if (integ === true || integ === 'true') return false;
  return true;
}

export function shouldShowLeads(passbook: Passbook, role: string): boolean {
  // Base não entra na lista legada de perfis. A página segue a mesma flag do menu (`hasLeads`).
  if (role === 'Base') {
    if (typeof passbook.hasLeads === 'boolean') return passbook.hasLeads;
    return true;
  }
  if (!passbook.notShowUsers) return true;
  return ['Client', 'Operator', 'Admin', 'Owner'].includes(role);
}

export function isMultiStoreLeads(passbook: Passbook): boolean {
  return Boolean(passbook.hasMultiStore);
}

export function isCardInstalled(lead: {
  deviceLibraryIdentifier?: unknown[];
  deviceToken?: unknown[];
  deviceGWallet?: unknown[];
}): boolean {
  return (
    (lead.deviceLibraryIdentifier?.length ?? 0) > 0 ||
    (lead.deviceToken?.length ?? 0) > 0 ||
    (lead.deviceGWallet?.length ?? 0) > 0
  );
}

type DeviceTokenItem = { typeAccess?: string } | string | null | undefined;

function tokenTypeAccess(token: DeviceTokenItem): string | undefined {
  if (!token || typeof token !== 'object') return undefined;
  const t = token.typeAccess;
  return typeof t === 'string' ? t : undefined;
}

/** iOS: Apple Wallet (`deviceLibraryIdentifier`) ou app com `deviceToken.typeAccess === APP-ios`. */
export function isIosDevice(lead: {
  deviceLibraryIdentifier?: unknown[];
  deviceToken?: DeviceTokenItem[];
}): boolean {
  if ((lead.deviceLibraryIdentifier?.length ?? 0) > 0) return true;
  return (lead.deviceToken ?? []).some((t) => tokenTypeAccess(t) === 'APP-ios');
}

/** Android: Google Wallet ou app com `deviceToken.typeAccess === APP-android`. */
export function isAndroidDevice(lead: {
  deviceGWallet?: unknown[];
  deviceToken?: DeviceTokenItem[];
}): boolean {
  if ((lead.deviceGWallet?.length ?? 0) > 0) return true;
  return (lead.deviceToken ?? []).some((t) => tokenTypeAccess(t) === 'APP-android');
}

/**
 * Label de plataforma no card/detalhe.
 * Prioriza arrays de device; Google Wallet permanece como label próprio (KPI Android inclui GWallet).
 */
export function walletPlatform(lead: {
  typeAccess?: string;
  deviceLibraryIdentifier?: unknown[];
  deviceToken?: DeviceTokenItem[];
  deviceGWallet?: unknown[];
}): string {
  if ((lead.deviceGWallet?.length ?? 0) > 0) return 'Google Wallet';
  if (isIosDevice(lead)) return 'iOS';
  if (isAndroidDevice(lead)) return 'Android';
  if (lead.typeAccess === 'APP-ios' || lead.typeAccess === 'iOS') return 'iOS';
  if (lead.typeAccess === 'APP-android' || lead.typeAccess === 'Android') return 'Android';
  return lead.typeAccess ?? 'Nenhuma';
}

export function operatorTransactionType(passbook: Passbook): 'input' | 'debit' | 'addPoints' {
  if (passbook.hasTransaction) return 'debit';
  // Cashback/pontos gravam action=addPoints no Log (selos usam input).
  if (passbook.hasCashback || passbook.hasPoints) return 'addPoints';
  return 'input';
}

/** API recalcula % / regra quando integrado é true (boolean ou string de produção). */
export function isRegraIntegrado(passbook: Pick<Passbook, 'regraCartao'>): boolean {
  const integ = passbook.regraCartao?.integrado as unknown;
  return integ === true || integ === 'true';
}

/** URL base da API pública (qrcode personalizado). */
export function publicApiBase(): string {
  const fromEnv = import.meta.env.VITE_PUBLIC_API_URL as string | undefined;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return 'http://localhost:3001/public';
}

/** Logo do passbook servida pela API pública (mesmo padrão do overview/owner). */
export function passbookLogoUrl(idPassbook: string): string {
  return `${publicApiBase()}/${idPassbook}/logo.png`;
}

/** Logo do nível no Wallet (`logo_<level>@2x.png`). Slug só com caracteres seguros. */
export function passbookTierLogoUrl(idPassbook: string, level: string): string {
  const slug = level.replace(/[^A-Za-z0-9_-]/g, '');
  return `${publicApiBase()}/${idPassbook}/logo_${slug}@2x.png`;
}

/** URL do front (convite / cartão). */
export function frontBase(): string {
  const fromEnv = import.meta.env.VITE_FRONT_URL as string | undefined;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  return 'https://fixe.club';
}
